import store, { getStore } from 'store';
import { siteInfoLookup, studyInfoLookup } from './formLookup';
import { formLookup } from './lookup';
import { SET_SITE_VALIDATION, SET_STUDY_VALIDATION, UPDATE_FORM_VALIDITY } from 'store/key';
import { declareInvKey, HAEndorsementKey } from 'constants/globalEnum';
import {
    CCO,
    CLUSTER_ADMIN,
    DISTRIBUTED,
    VERIFIED,
    SECRETARY,
    SUBMITTED,
    RE_SUBMITTED,
    CONFIRMED,
    FOR_REVIEW,
    REVIEWED,
    RETURN_FROM_IRB,
} from 'utility/lookup';

export const returnCondition = (userRole, statusId) => {
    /** when role = cco, valid = true */
    let valid = true;

    /** when role = secretary,workflow=HKWC/NTEC (secretary can see application only when flow=HKWC/NTEC (H, N))
     * 1) application status = submitted/resubmitted, secretary can return application to applicant
     * 2) application status = confirmed, secretary can return application to applicant
     */
    /** when role = secretary,workflow=Multi-cluster (M) && status is 'Returned From IRB' show 'Return' button*/
    if (userRole === SECRETARY) {
        valid = false;
        if ([RETURN_FROM_IRB, DISTRIBUTED, VERIFIED].includes(statusId)) {
            valid = true;
        }
        if ([SUBMITTED, RE_SUBMITTED, CONFIRMED, FOR_REVIEW, REVIEWED].includes(statusId)) {
            valid = true;
        }
    }

    // if the form is confirmed/Distributed/FOR_REVIEW/REVIEWED and the user role is CCO, disable the return button
    if (userRole === CCO && [CONFIRMED, DISTRIBUTED, FOR_REVIEW, REVIEWED].includes(statusId)) {
        valid = false;
    }

    /** if status = DISTRIBUTED and role = Cluster_admin */
    if (userRole === CLUSTER_ADMIN && statusId !== DISTRIBUTED) {
        valid = false;
    }

    return valid;
};

export const isSiteTeamMemberFormValid = (data) => {
    // no need to verify the delegation table
    return true;
};

export const isSitePersonalParticularFormValid = (applicationDetail, siteInfoData) => {
    const {
        title,
        surname,
        firstName,
        haStaff,
        haPosition,
        haDepartment,
        haHospitalCode,
        haOther,
        haClusterCode,
        uniStaff,
        uniPosition,
        uniDepartment,
        university,
        uniOther,
        student,
        studentFull,
        studentGraduate,
        studentDept,
        studentInst,
        supervisorName,
        supervisorDept,
        supervisorInst,
        siteSupervisorName,
        siteSupervisorDept,
        siteSupervisorHospCode,
        siteSupervisorOther,
        phone,
        email,
        mailing,
        coiInvolved,
        cvFileName,
    } = siteInfoData;

    if (checkNull(haStaff) && checkNull(uniStaff) && checkNull(student)) {
        return false;
    }

    // check if ha staff is checked
    if (haStaff === true) {
        if (checkNull(haPosition) || checkNull(haDepartment) || checkNull(haHospitalCode) || checkNull(haClusterCode)) {
            return false;
        }

        if (haHospitalCode === 'OTHERS' && checkNull(haOther)) {
            return false;
        }
    }

    // check if university staff is checked
    if (uniStaff === true) {
        if (checkNull(uniPosition) || checkNull(uniDepartment) || checkNull(university)) {
            return false;
        }

        if (university === 8 && checkNull(uniOther)) {
            return false;
        }
    }

    if (student === true) {
        if (
            checkNull(studentFull) ||
            checkNull(studentGraduate) ||
            checkNull(studentDept) ||
            checkNull(studentInst) ||
            checkNull(supervisorName) ||
            checkNull(supervisorDept) ||
            checkNull(supervisorInst) ||
            checkNull(siteSupervisorName) ||
            checkNull(siteSupervisorDept) ||
            checkNull(siteSupervisorHospCode)
        ) {
            return false;
        }

        if (siteSupervisorHospCode === 'OTHERS' && checkNull(siteSupervisorOther)) {
            return false;
        }
    }

    if (
        checkNull(title) ||
        checkNull(surname) ||
        checkNull(firstName) ||
        checkNull(phone) ||
        checkNull(email) ||
        checkNull(mailing) ||
        checkNull(cvFileName) ||
        checkNull(coiInvolved)
    ) {
        return false;
    }

    // check for coi table record should be exist
    if (coiInvolved === true) {
        const isExist = applicationDetail.coi.some(
            (item) => item?.siteInfo?.id?.toString() === siteInfoData.id.toString(),
        );

        if (!isExist) {
            return false;
        }
    }

    return true;
};

export const formCheckBeforeSave = () => {
    let result = { isPass: true, message: '' };

    const state = getStore().getState();
    const { postData } = state.applicationDetailPostData;

    const studyArticleLicenseData = postData?.studyArticleLicense ?? [];

    for (let i = 0; i < studyArticleLicenseData.length; i++) {
        const data = studyArticleLicenseData[i];

        if (data.sameHK === undefined) {
            result.isPass = false;
            result.message = 'Please complete table 11.2.3 Study article licence registration before saving the form';
        }
    }

    if (!result.isPass) {
        return result;
    }

    return result;
};

export const checkFormValid = (
    applicationDetail,
    skipSiteInfoValidation = false,
    skipStudyInfoValidation = false,
    siteInfoIdToCheck = '', // only siteInfo with specific will be checked if provided
) => {
    const state = getStore().getState();
    const validationObject = state.applicationDetailValid;
    const validationDetail = state.validation.study;
    const siteValidationDetail = state.validation.site;

    const { isApplicationFormDetailValid, siteInfoValid } = validationObject;

    const invalidSiteStatus = {};
    let invalidStudyKey = [];
    const messages = [];

    if (!skipStudyInfoValidation) {
        invalidStudyKey = Object.entries(isApplicationFormDetailValid)
            .map(([key, value]) => {
                if (!value) {
                    return key;
                } else {
                    return null;
                }
            })
            .filter((item) => item !== null);

        invalidStudyKey.forEach((item, index) => {
            const isLastIndex = index + 1 === invalidStudyKey.length;

            if (index === 0) {
                messages.push(
                    <div
                        key={`header-${index}`}
                        style={{
                            fontWeight: 'bold',
                            fontSize: 16,
                            paddingBottom: 5,
                            borderBottom: '1px solid #dcdcdc',
                        }}
                    >
                        Study Info
                    </div>,
                );
            }

            const validationFailList = Object.entries(validationDetail?.[item] ?? {})
                .filter(([key, value]) => !value)
                .map((item) => {
                    return item[0];
                });

            messages.push(
                <div key={`study-info-${index}`} style={{ paddingBottom: isLastIndex ? 20 : 0 }}>
                    <div style={{ marginTop: 10, display: 'flex', fontWeight: 'bold' }}>
                        <div style={{ width: 25 }}>{index + 1}.</div> {formLookup(item)}
                    </div>
                    <div style={{ paddingLeft: 25, marginTop: 5 }}>
                        {validationFailList
                            .map((validationKey) => {
                                return studyInfoLookup()?.[validationKey]?.number ?? 'undefined';
                            })
                            .join(', ')}
                    </div>
                </div>,
            );
        });
    }

    if (!skipSiteInfoValidation) {
        let sitesToCheck = siteInfoValid;

        // if siteInfoIdToCheck provided, only the site id provided will have validation check
        if (siteInfoIdToCheck) {
            sitesToCheck = {
                [siteInfoIdToCheck]: siteInfoValid[siteInfoIdToCheck],
            };
        }

        // adding for temp fix
        delete sitesToCheck['studyInfo'];

        Object.entries(sitesToCheck).forEach(([siteInfoId, siteValidationObject]) => {
            Object.entries(siteValidationObject).forEach(([key, isValid]) => {
                if (!isValid) {
                    if (!invalidSiteStatus?.[siteInfoId]) {
                        invalidSiteStatus[siteInfoId] = [];
                    }
                    invalidSiteStatus[siteInfoId].push(key);
                }
            });
        });

        Object.entries(invalidSiteStatus).forEach(([siteInfoId, siteError], index) => {
            if (siteInfoId.toString() !== '-1') {
                siteError.forEach((item, siteErrorIndex) => {
                    const isLastIndex = siteError.length === siteErrorIndex + 1;

                    if (siteErrorIndex === 0) {
                        const siteName =
                            applicationDetail.siteInfo.find((item) => item.id.toString() === siteInfoId.toString())
                                ?.institution ?? 'undefined';
                        messages.push(
                            <div
                                key={`header-site-${siteInfoId}-${siteErrorIndex}`}
                                style={{
                                    fontWeight: 'bold',
                                    fontSize: 16,
                                    paddingBottom: 5,
                                    borderBottom: '1px solid #dcdcdc',
                                }}
                            >
                                Site ({siteName})
                            </div>,
                        );
                    }

                    const validationFailList = Object.entries(siteValidationDetail?.[item]?.[siteInfoId] ?? {})
                        .filter(([key, value]) => !value)
                        .map((item) => {
                            return item[0];
                        });

                    messages.push(
                        <div
                            key={`site-info-${siteInfoId}-${siteErrorIndex}`}
                            style={{
                                paddingBottom: isLastIndex ? 20 : 0,
                            }}
                        >
                            <div
                                style={{
                                    marginTop: 10,

                                    display: 'flex',
                                    fontWeight: 'bold',
                                }}
                            >
                                <div style={{ width: 25 }}>{siteErrorIndex + 1}.</div> {formLookup(item)}
                            </div>
                            <div style={{ paddingLeft: 25, marginTop: 5 }}>
                                {validationFailList
                                    .map((validationKey) => {
                                        return siteInfoLookup()?.[validationKey]?.number ?? 'undefined';
                                    })
                                    .join(', ')}
                            </div>
                        </div>,
                    );
                });
            }
        });
    }

    let isFormValid = true;

    if (!skipStudyInfoValidation) {
        if (invalidStudyKey.length > 0) {
            isFormValid = false;
        }
    }

    if (!skipSiteInfoValidation) {
        if (Object.entries(invalidSiteStatus).length > 0) {
            isFormValid = false;
        }
    }

    return {
        messages: messages,
        isFormValid: isFormValid,
    };
};

export const isSiteOutlineFormValid = (applicationDetail, siteInfoData) => {
    const dataChecked = applicationDetail.applicationForm['p1ConductedHa'];

    if (dataChecked) {
        const isExist = applicationDetail.studySiteApply.some(
            (item) => item?.siteInfo?.id?.toString() === siteInfoData.id.toString(),
        );

        if (!isExist) {
            return false;
        }
    }

    // wait for ha feedback
    return true;
};

export const isSiteStudyDetailFormValid = (data) => {
    const { p2NumSubject, p2SubjectRecruit } = data;

    if (checkNull(p2NumSubject) || checkNull(p2SubjectRecruit)) {
        return false;
    }

    return true;
};

export const checkNull = (value) => {
    if (value === undefined || value === null || value === '') {
        return true;
    }

    return false;
};

export const isSiteStudyDetailForProspectiveFormValid = (applicationDetail, data) => {
    const { p2NumOfExtra, p2OtherInvestigator, p2WhomWhere } = data;
    let { p2StudyDesign } = applicationDetail.applicationForm;

    // no need to input value when p2StudyDesign === 'P' in application detail
    if (p2StudyDesign === 'R' || p2StudyDesign === null) {
        return true;
    }

    if (checkNull(p2NumOfExtra) || checkNull(p2OtherInvestigator)) {
        return false;
    }

    if (p2OtherInvestigator === true) {
        if (checkNull(p2WhomWhere)) {
            return false;
        }
    }

    return true;
};

export const isSiteEthicalFormValid = (applicationDetail, data) => {
    const { p3InformConsentOthersReason, p3InformConsentPrincipalInv } = data;
    const { p3Methodology } = applicationDetail.applicationForm;

    if (p3Methodology === 'N') {
        return true;
    }

    if (checkNull(p3InformConsentPrincipalInv)) {
        return false;
    }

    if (p3InformConsentPrincipalInv === 3 && checkNull(p3InformConsentOthersReason)) {
        return false;
    }

    return true;
};

export const isSiteBudgetFormValid = (data) => {
    const {
        p4ExtraHAResources,
        p4ExtraHAResourcesDetails,
        p4ExtraHAResourcesAffect,
        p4Reimburse,
        p4ReimburseFormat,
        p4ReimburseReason,
    } = data;

    if (checkNull(p4ExtraHAResources) || checkNull(p4Reimburse)) {
        return false;
    }

    if (p4ExtraHAResources === true && (checkNull(p4ExtraHAResourcesDetails) || checkNull(p4ExtraHAResourcesAffect))) {
        return false;
    }

    if (p4Reimburse === true && checkNull(p4ReimburseFormat)) {
        return false;
    }

    if (p4Reimburse === false && checkNull(p4ReimburseReason)) {
        return false;
    }

    return true;
};

export const checkSiteInfoStudent = (siteInfoList, postDataSiteInfo) => {
    let res = false;
    for (let i of siteInfoList) {
        const index = postDataSiteInfo.findIndex((item) => item.id === i.id);
        if (i.student) {
            if (index !== -1) {
                res = postDataSiteInfo[index].student;
            } else {
                res = true;
            }
            if (res) {
                break;
            }
        }
    }
    return res;
};

export const checkStudyInfoDocumentUploadValid = ({ applicationDetail, isAmendment, isStudent, formId }) => {
    const validationKey = 'documentUploadValid';
    const state = getStore().getState();
    const validationDetail = state.validation.study;
    let studyInfoValidation = validationDetail[validationKey];

    let supportingDocumentRows = []; /** type = 7 */
    for (let i of applicationDetail.uploadDocuments) {
        if (i.type === 7) {
            supportingDocumentRows.push(i);
        }
    }

    /** check student info */
    if (isStudent && !supportingDocumentRows.length && !isAmendment) {
        studyInfoValidation.docUpload_7 = false;
    } else {
        studyInfoValidation.docUpload_7 = true;
    }
    const isUploadDocFormValid =
        Object.keys(studyInfoValidation).filter((key) => {
            return studyInfoValidation[key] === false;
        }).length === 0;

    store.dispatch({
        type: UPDATE_FORM_VALIDITY,
        payload: {
            type: 'study',
            applicationFormId: formId,
            formKey: 'documentUploadValid',
            isValid: isUploadDocFormValid,
        },
    });
    updateStudyValidation({ validationKey, studyInfoValidation });
};

export const isDocumentUploadValid = (applicationDetail, siteInfoData) => {
    /** check siteInfo */
    const siteInfoId = siteInfoData.id;
    const validationKey = 'documentUploadValid';
    let valids = {};
    const setValid = (key, valid) => {
        valids = {
            ...valids,
            [key]: valid,
        };
    };

    let valid = true;
    let declarationRows = []; /** type = 5 */
    let signedApplicationFormRows = []; /** type = 10 */
    let indemnityAgreementRows = []; /** type = 6 */

    for (let i of applicationDetail.uploadDocuments) {
        if (i.type === 5 && i.siteInfo?.id === siteInfoData.id) {
            declarationRows.push(i);
        }
        if (i.type === 10 && i.siteInfo?.id === siteInfoData.id) {
            signedApplicationFormRows.push(i);
        }
        if (i.type === 6 && i.siteInfo?.id === siteInfoData.id) {
            indemnityAgreementRows.push(i);
        }
    }

    if (declarationRows.length) {
        setValid('docUpload_5', true);
    } else {
        valid = false;
        setValid('docUpload_5', false);
    }
    if (signedApplicationFormRows.length) {
        setValid('docUpload_10', true);
    } else {
        valid = false;
        setValid('docUpload_10', false);
    }

    // if (applicationDetail.applicationForm.p4Sponsor) {
    //     if (indemnityAgreementRows.length) {
    //         setValid('docUpload_6', true);
    //     } else {
    //         valid = false;
    //         setValid('docUpload_6', false);
    //     }
    // }

    store.dispatch({
        type: SET_SITE_VALIDATION,
        payload: {
            siteInfoId,
            [validationKey]: valids,
        },
    });
    return valid;
};

/** Check valid for Declaration & Endorsement start */
export const updateStudyValidation = ({ validationKey, studyInfoValidation }) => {
    store.dispatch({
        type: SET_STUDY_VALIDATION,
        payload: {
            [validationKey]: studyInfoValidation,
        },
    });
};
export const updateSiteValidation = ({ siteInfoId, validationKey, siteInfoValidation }) => {
    store.dispatch({
        type: SET_SITE_VALIDATION,
        payload: {
            siteInfoId,
            [validationKey]: siteInfoValidation,
        },
    });
};

export const declareStudentAutoFilledRoles = ['Academic Supervisor', 'Site Supervisor'];

export const declareInvAutoFilledRoles = ['Principal Investigator', 'Co-investigator'];

export const columnKeys = ['role', 'title', 'name', 'position', 'responsibility'];

export const checkTableColumnCompleted = (rows) => {
    let valid = true;
    for (let i of rows) {
        for (let n in i) {
            if (columnKeys.includes(n) && [undefined, '', null].includes(i[n])) {
                valid = false;
                break;
            }
        }
        if (!valid) {
            break;
        }
    }
    return valid;
};

export const checkDeclarationAndEndorsementCompleted = ({
    tableAColumnCompleted = true,
    HAEndorsementRows,
    universitiesEndorsementRows,
    siteInfoId,
}) => {
    const validationKey = 'declarationAndEndorsementValid';
    /** set site info inside valid for A/B/C/D table */
    let valids = {};
    const setValid = (key, valid) => {
        valids = {
            ...valids,
            [key]: valid,
        };
    };

    let completed = true;
    /** table A */
    if (!tableAColumnCompleted) {
        completed = false;
        setValid(declareInvKey, false);
    } else {
        setValid(declareInvKey, true);
    }

    /** table B/C/D: logic check on completing either part B/C/D only */
    if (HAEndorsementRows.length || universitiesEndorsementRows.length) {
        setValid(HAEndorsementKey, true);
    } else {
        completed = false;
        setValid(HAEndorsementKey, false);
    }

    updateSiteValidation({ siteInfoId, validationKey, siteInfoValidation: valids });

    return completed;
};

export const getExistedInDBRows = (applicationDetail, siteInfoData) => {
    let HAEndorsementRows = [],
        universitiesEndorsementRows = [],
        HAUniversitiesEndorsementRows = [];
    for (let i of applicationDetail.declare) {
        if (i.siteInfo?.id === siteInfoData.id) {
            switch (i.endorse) {
                case 'H':
                    HAEndorsementRows.push(i);
                    break;
                case 'U':
                    universitiesEndorsementRows.push(i);
                    break;
                case 'C':
                    HAUniversitiesEndorsementRows.push(i);
                    break;

                default:
                    break;
            }
        }
    }
    return {
        HAEndorsementRows: HAEndorsementRows,
        universitiesEndorsementRows: universitiesEndorsementRows,
        HAUniversitiesEndorsementRows: HAUniversitiesEndorsementRows,
    };
};

export const mapCurrentSiteRows = (rows, siteInfoId) => {
    let newRows = [];
    rows?.forEach((item) => {
        if (item.siteInfo?.id === siteInfoId) {
            newRows.push(item);
        }
    });
    return newRows;
};

export const isDeclarationAndEndorsementValid = (applicationDetail, siteInfoData, formId) => {
    let rolesCompleted = true;
    const declareInvRows = mapCurrentSiteRows(applicationDetail.declareInv);
    const declareStudentRows = mapCurrentSiteRows(applicationDetail.declareStudent);
    // check row that has the smallest id no.
    const noPIs = declareInvRows?.filter((entry) => entry.role !== 'Principal Investigator');
    const sortedDeclareInv =
        declareInvRows?.filter((entry) => entry.role === 'Principal Investigator')?.sort((a, b) => a?.id - b?.id) || [];
    const tempDeclareInvRows = sortedDeclareInv?.concat(noPIs);
    const declareInvValid = checkTableColumnCompleted(tempDeclareInvRows ?? []);
    const declareStudentValid = checkTableColumnCompleted(declareStudentRows);

    let HAEndorsementRows = [];
    let universitiesEndorsementRows = [];

    /** check Declaration And Endorsement for completeness */
    let currentStatus = rolesCompleted;
    if (currentStatus) {
        const rows = getExistedInDBRows(applicationDetail, siteInfoData);
        HAEndorsementRows = rows.HAEndorsementRows;
        universitiesEndorsementRows = rows.universitiesEndorsementRows;

        const siteInfoId = siteInfoData.id;
        currentStatus = checkDeclarationAndEndorsementCompleted({
            tableAColumnCompleted: declareInvValid && declareStudentValid,
            HAEndorsementRows,
            universitiesEndorsementRows,
            siteInfoId,
        });
    }

    return rolesCompleted && currentStatus;
};
/** Check valid for Declaration & Endorsement end */

export const getSiteInfoValidationObject = (applicationDetail, siteData, formId) => {
    return {
        teamMember: isSiteTeamMemberFormValid(siteData),
        personalParticulars: isSitePersonalParticularFormValid(applicationDetail, siteData),
        outlineOfApplication: isSiteOutlineFormValid(applicationDetail, siteData),
        studyDetails: isSiteStudyDetailFormValid(siteData),
        studyDetailsForProspectiveStudy: isSiteStudyDetailForProspectiveFormValid(applicationDetail, siteData),
        ethicalImplications: isSiteEthicalFormValid(applicationDetail, siteData),
        budgetResources: isSiteBudgetFormValid(siteData),
        documentUploadValid: isDocumentUploadValid(applicationDetail, siteData),
        declarationAndEndorsementValid: isDeclarationAndEndorsementValid(applicationDetail, siteData, formId),
    };
};
